// API 엔드포인트 중앙 관리
// 모든 API 경로를 여기서 정의하여 일관성과 유지보수성 확보

export const API_ENDPOINTS = {
  // 사용자 관련
  USERS: {
    CURRENT: '/users/current',
    REGISTER: '/users/register',
    CHECK_EMAIL: '/users/check-email',
    CHECK_BUSINESS_NUMBER: '/users/check-business-number',
    UPDATE_PROFILE: (id: number) => `/users/${id}`,
    UPDATE_PASSWORD: (id: number) => `/users/${id}/password`,
  },

  // 인증 관련
  AUTH: {
    LOGIN: '/login',
    STORES_AUTH: '/stores/auth',
  },

  // 매장 관련
  STORES: {
    INFO: (storeId: number) => `/stores/${storeId}`,
    SETTINGS: (storeId: number) => `/stores/${storeId}/settings`,
    ME_STORES: '/stores/me/stores',
  },

  // 다중 매장 집계·인사이트 (Phase 1, 4.1~4.8 분석·리포트)
  MULTI_STORE: {
    SUMMARY: '/multi-store/summary',
    ATTENTION_STORES: '/multi-store/attention-stores',
    PERFORMANCE: '/multi-store/performance',
    STORE_HEALTH: (storeId: number) => `/multi-store/stores/${storeId}/health`,
    RECOMMENDED_ACTIONS: '/multi-store/recommended-actions',
    GOAL_VS_ACTUAL: '/multi-store/goal-vs-actual',
    /** 월별 목표 목록(GET)·저장(POST) */
    GOALS: '/multi-store/goals',
    TRENDS: '/multi-store/trends',
    COST_MARGIN: '/multi-store/cost-margin',
    BENCHMARK: '/multi-store/benchmark',
    PROMO: '/multi-store/promo',
    /** 프로모 캠페인 CRUD (POST/PATCH/DELETE/GET 목록) */
    PROMO_CAMPAIGNS: '/multi-store/promo/campaigns',
    FRANCHISE_USAGE: '/multi-store/franchise-usage',
    REPORT_DOWNLOAD: '/multi-store/report/download',
    EXPORT: '/multi-store/export',
  },

  // 주문 관련
  ORDERS: {
    LIST: '/orders',
    DETAIL: (id: number) => `/orders/${id}`,
    UPDATE_STATUS: (id: number) => `/orders/${id}/status`,
    ADD_NOTE: (id: number) => `/orders/${id}/notes`,
    STATISTICS: '/orders/statistics',
    EXPORT: '/orders/export',
    DEDUCT_INVENTORY: (orderId: number) => `/inventory/deduct-for-order/${orderId}`,
  },

  // 재고 관련
  INVENTORY: {
    INGREDIENTS: '/ingredients',
    INGREDIENT_DETAIL: (id: number) => `/ingredients/${id}`,
    INGREDIENT_ADJUST: (ingredientId: number) => `/ingredients/${ingredientId}/adjust`,
    SUPPLIERS: '/suppliers',
    SUPPLIER_DETAIL: (id: number) => `/suppliers/${id}`,
    STOCK_TRANSACTIONS: '/stock-transactions',
    STOCK_TRANSACTIONS_WITH_BATCHES: (storeId: number, ingredientId: number) => 
      `/stock-transactions-with-batches?storeId=${storeId}&ingredientId=${ingredientId}`,
    EXPIRING_INGREDIENTS: '/expiring-ingredients',
    INBOUND_WITH_BATCHES: '/inbound-with-batches',
    OUTBOUND_WITH_FIFO: '/outbound-with-fifo',
    OUTBOUND_FROM_BATCH: '/outbound-from-batch',
    EXPIRED_BATCHES: '/expired-batches',
    BATCH_TRANSACTIONS: (storeId: number, ingredientId: number) => 
      `/batch-transactions?storeId=${storeId}&ingredientId=${ingredientId}`,
    CLEANUP_EXPIRED: '/cleanup-expired',
  },

  // 메뉴 관련
  MENUS: {
    LIST: '/menus',
    DETAIL: (id: number) => `/menus/${id}`,
    CREATE: '/menus',
    UPDATE: (id: number) => `/menus/${id}`,
    DELETE: (id: number) => `/menus/${id}`,
    UPDATE_STATUS: (id: number) => `/menus/${id}/status`,
    CATEGORIES: '/menu-categories',
    DELETE_CATEGORY: (category: string) => `/menu-categories/${encodeURIComponent(category)}`,
  },

  // 작업대 관련
  WORK_STATIONS: {
    LIST: '/work-stations',
    CREATE: '/work-stations',
    UPDATE: (id: number) => `/work-stations/${id}`,
    DELETE: (id: number) => `/work-stations/${id}`,
  },

  // 공급망 관련
  SUPPLY_CHAIN: {
    PURCHASE_ORDERS: '/purchase-orders',
    PURCHASE_ORDER_DETAIL: (id: number) => `/purchase-orders/${id}`,
    ORDER_ITEMS_FOR_SUPPLIER: (supplierId: number, storeId: number) => 
      `/order-items-for-supplier?supplierId=${supplierId}&storeId=${storeId}`,
    CAN_ORDER_FOR_SUPPLIER: (supplierId: number, storeId: number) => 
      `/can-order-for-supplier?supplierId=${supplierId}&storeId=${storeId}`,
    PROCESS_INBOUND: (orderId: number) => `/purchase-orders/${orderId}/process-inbound`,
  },

  // 분석 관련
  ANALYTICS: {
    INGREDIENTS_FOR_ANALYTICS: (storeId: number) => `/ingredients-for-analytics?storeId=${storeId}`,
    STOCK_TRANSACTIONS_FOR_ANALYTICS: (storeId: number, startDate: string, endDate: string) => 
      `/stock-transactions-for-analytics?storeId=${storeId}&startDate=${startDate}&endDate=${endDate}`,
    MENUS_FOR_ANALYTICS: (storeId: number) => `/menus-for-analytics?storeId=${storeId}`,
    MENU_SALES_ANALYTICS: (storeId: number, startDate: string, endDate: string) => 
      `/menu-sales-analytics?storeId=${storeId}&startDate=${startDate}&endDate=${endDate}`,
    DAILY_SUMMARY: (storeId: number, date: string) => `/daily-summary?storeId=${storeId}&date=${date}`,
    SALES_FROM_ORDERS: (storeId: number, date: string) => `/sales-from-orders?storeId=${storeId}&date=${date}`,
    // 매장분석
    POPULAR_MENUS: (storeId: number, startDate: string, endDate: string) => 
      `/analytics/store-analysis/popular-menus/${storeId}?startDate=${startDate}&endDate=${endDate}`,
    POPULAR_OPTIONS: (storeId: number, startDate: string, endDate: string) => 
      `/analytics/store-analysis/popular-options/${storeId}?startDate=${startDate}&endDate=${endDate}`,
    POPULAR_INGREDIENTS: (storeId: number, startDate: string, endDate: string) => 
      `/analytics/store-analysis/popular-ingredients/${storeId}?startDate=${startDate}&endDate=${endDate}`,
    REVENUE_TREND: (storeId: number, startDate: string, endDate: string) => 
      `/analytics/store-analysis/revenue-trend/${storeId}?startDate=${startDate}&endDate=${endDate}`,
  },

  // 푸시 알림 관련
  PUSH_NOTIFICATIONS: {
    SUBSCRIBE: '/push/subscribe',
    UNSUBSCRIBE: (userId: number) => `/push/unsubscribe/${userId}`,
    SETTINGS: (userId: number) => `/push/settings/${userId}`,
    VAPID_KEY: '/push/vapid-key',
  },

  // 관리자 관련 (adminApi baseURL에 /admin이 포함되므로 /api 없이 시작)
  ADMIN: {
    AUTH: {
      LOGIN: '/auth/login',
      LOGOUT: '/auth/logout',
      PROFILE: '/auth/profile',
    },
    DASHBOARD: {
      MAIN: '/dashboard',
      STATS: '/dashboard/stats',
      SERVER_STATUS: '/dashboard/server-status',
    },
    USERS: {
      LIST: '/users',
      DETAIL: (id: number) => `/users/${id}`,
      CREATE: '/users',
      UPDATE: (id: number) => `/users/${id}`,
      DELETE: (id: number) => `/users/${id}`,
      TOGGLE_STATUS: (id: number) => `/users/${id}/toggle-status`,
      APPROVE: (userId: number) => `/users/${userId}/approve`,
    },
    STORES: {
      LIST: '/stores',
      DETAIL: (id: number) => `/stores/${id}`,
      UPDATE_STATUS: (id: number) => `/stores/${id}/update-status`,
      LOGS: (id: number) => `/stores/${id}/logs`,
      BACKUPS: (id: number) => `/stores/${id}/backups`,
      CREATE_BACKUP: (id: number) => `/stores/${id}/create-backup`,
      RESTORE_BACKUP: (id: number, backupId: number) => `/stores/${id}/restore-backup/${backupId}`,
      DELETE_BACKUP: (id: number, backupId: number) => `/stores/${id}/delete-backup/${backupId}`,
      ANALYTICS: (id: number) => `/stores/${id}/analytics`,
    },
    BACKUPS: {
      LIST: '/backups',
      DETAIL: (id: number) => `/backups/${id}`,
      DELETE: (id: number) => `/backups/${id}`,
    },
    NOTIFICATIONS: {
      SETTINGS: '/notifications/settings',
      UPDATE_SETTINGS: '/notifications/settings',
      LOGS: '/notifications/logs',
    },
    API_USAGE: {
      STATS: '/api-usage/stats',
      LOGS: '/api-usage/logs',
    },
    SECURITY: {
      EVENTS: '/security-events',
      EVENT_DETAIL: (id: number) => `/security-events/${id}`,
      RESOLVE_EVENT: (id: number) => `/security-events/${id}/resolve`,
    },
    SYSTEM: {
      LOGS: '/system-logs',
      LOG_DETAIL: (id: number) => `/system-logs/${id}`,
    },
    FAILED_ORDERS: {
      LIST: '/failed-orders',
      DETAIL: (id: number) => `/failed-orders/${id}`,
      SUMMARY: '/failed-orders/summary',
      RETRY: (id: number) => `/failed-orders/${id}/retry`,
      UPDATE_STATUS: (id: number) => `/failed-orders/${id}/status`,
    },
    USER_APPROVALS: {
      PENDING: '/pending-users',
      REJECTED: '/rejected-users',
      APPROVED: '/approved-users',
      SUMMARY: '/pending-users/summary',
    },
    MONITORING: {
      STATS: '/monitoring/stats',
      LOGS: '/monitoring/logs',
      ERROR_PATTERNS: '/monitoring/error-patterns',
      ALERTS: '/monitoring/alerts',
      REALTIME: '/monitoring/realtime',
      PATH_DUPLICATIONS: '/monitoring/path-duplications',
      RESOLVE_ALERT: (alertId: string) => `/monitoring/alerts/${alertId}/resolve`,
    },
    DIAGNOSTIC_REPORTS: {
      LIST: '/diagnostic-reports',
      DETAIL: (id: number) => `/diagnostic-reports/${id}`,
      SUMMARY: '/diagnostic-reports/summary',
    },
  },

  // 테스트 관련
  TEST: {
    CONNECTION: '/test',
  },
} as const;

// 타입 안전성을 위한 타입 정의
export type ApiEndpoint = typeof API_ENDPOINTS;
export type EndpointKey = keyof ApiEndpoint;

// 엔드포인트 검증 함수
export const validateEndpoint = (endpoint: string): boolean => {
  // /api로 시작하지 않아야 함 (baseURL에 이미 포함됨)
  if (endpoint.startsWith('/api/')) {
    console.warn(`⚠️ 엔드포인트가 /api로 시작합니다: ${endpoint}`);
    return false;
  }
  
  // /로 시작해야 함
  if (!endpoint.startsWith('/')) {
    console.warn(`⚠️ 엔드포인트가 /로 시작하지 않습니다: ${endpoint}`);
    return false;
  }
  
  return true;
};

// 엔드포인트 빌더 함수
export const buildEndpoint = (endpoint: string): string => {
  if (!validateEndpoint(endpoint)) {
    throw new Error(`잘못된 엔드포인트 형식: ${endpoint}`);
  }
  return endpoint;
}; 